"use client";

import { useState } from "react";
import Link from "next/link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

export default function RegisterPage() {
  const [form, setForm] = useState({
    companyName: "",
    companyAddress: "",
    companyTaxId: "",
    contactName: "",
    contactPhone: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  function update(field: keyof typeof form, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    const res = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: form.email,
        password: form.password,
        companyName: form.companyName,
        companyAddress: form.companyAddress,
        companyTaxId: form.companyTaxId,
        contactName: form.contactName,
        contactPhone: form.contactPhone,
      }),
    });

    setLoading(false);

    if (res.ok) {
      setSubmitted(true);
      return;
    }

    const data = await res.json();
    setError(data.error ?? "Registration failed.");
  }

  if (submitted) {
    return (
      <Card className="w-full max-w-md text-center">
        <CardContent className="pt-8 pb-8 space-y-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <span className="text-primary text-2xl">✓</span>
          </div>
          <h2 className="text-xl font-semibold">Request submitted</h2>
          <p className="text-sm text-muted-foreground">
            Your account request has been received. An administrator will review and approve
            your account shortly. You will be able to sign in once approved.
          </p>
          <Link href="/login" className="text-primary hover:underline text-sm">
            Back to sign in
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-xl">
      <CardHeader className="text-center">
        <div className="flex items-center justify-center mb-2">
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-xl">K</span>
          </div>
        </div>
        <CardTitle className="text-2xl font-serif">Request Access</CardTitle>
        <CardDescription>Fill in your company and contact details to request an account.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">Company Information</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  autoComplete="organization"
                  value={form.companyName}
                  onChange={(e) => update("companyName", e.target.value)}
                                    required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="companyAddress">Company Address *</Label>
                <Input
                  id="companyAddress"
                  autoComplete="off"
                  value={form.companyAddress}
                  onChange={(e) => update("companyAddress", e.target.value)}
                                    required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="companyTaxId">Tax ID (PIB) *</Label>
                <Input
                  id="companyTaxId"
                  autoComplete="off"
                  value={form.companyTaxId}
                  onChange={(e) => update("companyTaxId", e.target.value)}
                                    required
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">Contact Person</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="contactName">Full Name *</Label>
                <Input
                  id="contactName"
                  autoComplete="name"
                  value={form.contactName}
                  onChange={(e) => update("contactName", e.target.value)}
                                    required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="contactPhone">Phone Number *</Label>
                <Input
                  id="contactPhone"
                  type="tel"
                  autoComplete="tel"
                  value={form.contactPhone}
                  onChange={(e) => update("contactPhone", e.target.value)}
                                    required
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">Login Credentials</p>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                                    required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                                    required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={form.confirmPassword}
                  onChange={(e) => update("confirmPassword", e.target.value)}
                  required
                />
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Submitting…" : "Submit Request"}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link href="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  );
}
